import { includes, round } from 'lodash';
import React, { FC, useEffect } from 'react';
import { useService } from 'react-service-locator';
import { SearchService } from '../../services/search.service';
import './FilterSection.scss';
import Slider from 'rc-slider';
import { useTranslation } from 'react-i18next';
type Gender = 'male' | 'female';

interface IFilters {
    gender?: Gender[];
    minAge: number;
    maxAge: number;
    minPublicationYear: number;
    maxPublicationYear: number;
    studyDesign?: ('meta-analysis' | 'RCT')[];
    studyGroupSize?: number[];
    studyPeriod?: number[];
    amountOfCitations?: number[];
    ethnicity?: (
        | 'NorthernAfrican'
        | 'SouthernEuropean'
        | 'CentralEuropean'
        | 'Asian'
        | 'SouthAfrican'
        | 'SouthAsian'
        | 'WesternEuropean'
        | 'SouthwestAsian'
        | 'LatinAmerican'
        | 'WestAfrican'
        | 'NorthernEuropean'
        | 'Oceanian'
        | 'EastAfrican'
        | 'Balkan'
        | 'HornOfAfrica'
        | 'NorthAmerican'
        | 'CentralAfrican'
        | 'Baltic'
        | 'EasternEuropean'
        | 'Caucasian'
    )[];
    countries?: string[];
}

type RegionGroup =
    | {
          label: string;
          type: 'ethnicity';
          codes: NonNullable<IFilters['ethnicity']>[number][];
      }
    | {
          label: string;
          type: 'countrylist';
          countries: string[];
      };

interface RegionBlock {
    id: string;
    region: string;
    groups: RegionGroup[];
    noSubgroups?: boolean;
    noAutoCountries?: boolean;
    autoEthnicities?: NonNullable<IFilters['ethnicity']>[number][];
}

const calculatePaddingPercentageLeft = (min: number, max: number, vMin: number, vMax: number) => {
    const range = max - min;
    const rangeToLeft = vMin - min;
    const rangeToRight = vMax - min;
    const rangeToAvg = (rangeToLeft + rangeToRight) / 2;
    return (rangeToAvg / range) * 100;
};

const FilterSection: FC = () => {
    const { t } = useTranslation();
    const searchService = useService(SearchService);
    const [openRegions, setOpenRegions] = React.useState<Record<string, boolean>>({});

    const selectAge = (age: number[]) => {
        const sliderAgeLabel = document.getElementById('slider-age-label');
        if (!sliderAgeLabel) {
            return;
        }
        const minAge = age[0];
        const maxAge = age[1];
        const currentFilters: IFilters = {
            ...searchService.state.filters,
            minAge: minAge,
            maxAge: maxAge,
            gender: searchService.state.filters.gender ? [...searchService.state.filters.gender] : undefined,
            studyDesign: searchService.state.filters.studyDesign
                ? [...searchService.state.filters.studyDesign]
                : undefined,
            studyGroupSize: searchService.state.filters.studyGroupSize
                ? [...searchService.state.filters.studyGroupSize]
                : undefined,
            studyPeriod: searchService.state.filters.studyPeriod
                ? [...searchService.state.filters.studyPeriod]
                : undefined,
            amountOfCitations: searchService.state.filters.amountOfCitations
                ? [...searchService.state.filters.amountOfCitations]
                : undefined,
            ethnicity: searchService.state.filters.ethnicity ? [...searchService.state.filters.ethnicity] : undefined,
            countries: searchService.state.filters.countries ? [...searchService.state.filters.countries] : undefined,
        };
        searchService.setFilters(currentFilters);
    };
    const selectPublicationYear = (publicationYears: number[]) => {
        const sliderPublicationYearLabel = document.getElementById('slider-publication-year-label');
        if (!sliderPublicationYearLabel) {
            return;
        }
        const minPublicationYear =
            publicationYears[0] > 2000
                ? round(((publicationYears[0] - 2000) / 99) * (SearchService.MAX_PUBLICATION_YEAR - 2000) + 2000)
                : publicationYears[0];
        const maxPublicationYear =
            publicationYears[1] > 2000
                ? round(((publicationYears[1] - 2000) / 99) * (SearchService.MAX_PUBLICATION_YEAR - 2000) + 2000)
                : publicationYears[1];
        const currentFilters: IFilters = {
            ...searchService.state.filters,
            gender: searchService.state.filters.gender ? [...searchService.state.filters.gender] : undefined,
            studyDesign: searchService.state.filters.studyDesign
                ? [...searchService.state.filters.studyDesign]
                : undefined,
            studyGroupSize: searchService.state.filters.studyGroupSize
                ? [...searchService.state.filters.studyGroupSize]
                : undefined,
            studyPeriod: searchService.state.filters.studyPeriod
                ? [...searchService.state.filters.studyPeriod]
                : undefined,
            amountOfCitations: searchService.state.filters.amountOfCitations
                ? [...searchService.state.filters.amountOfCitations]
                : undefined,
            ethnicity: searchService.state.filters.ethnicity ? [...searchService.state.filters.ethnicity] : undefined,
            countries: searchService.state.filters.countries ? [...searchService.state.filters.countries] : undefined,
        };
        currentFilters.minPublicationYear = minPublicationYear;
        currentFilters.maxPublicationYear = maxPublicationYear;
        searchService.setFilters(currentFilters);
    };
    const toggle = <T,>(arr: T[] | undefined, value: T): T[] => {
        const a = arr ? Array.from(arr) : [];
        return a.includes(value) ? a.filter((v) => v !== value) : [...a, value];
    };

    const emptyToUndefined = <T,>(arr: readonly T[] | undefined): T[] | undefined =>
        arr ? (arr.length === 0 ? undefined : Array.from(arr)) : undefined;
    const selectGender = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value as 'male' | 'female';
        const f = { ...searchService.state.filters } as IFilters;
        f.gender = event.target.checked ? toggle(f.gender, value) : (f.gender ?? []).filter((v) => v !== value);

        f.gender = emptyToUndefined(f.gender);
        searchService.setFilters(f);
    };

    // STUDY DESIGN
    const selectStudyDesign = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value as 'meta-analysis' | 'RCT';
        const f = { ...searchService.state.filters } as IFilters;
        f.studyDesign = event.target.checked
            ? toggle(f.studyDesign, value)
            : (f.studyDesign ?? []).filter((v) => v !== value);

        f.studyDesign = emptyToUndefined(f.studyDesign);
        searchService.setFilters(f);
    };

    // GROUP SIZE (numbers)
    const selectStudySize = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseInt(event.target.value, 10);
        const f = { ...searchService.state.filters } as IFilters;
        f.studyGroupSize = event.target.checked
            ? toggle(f.studyGroupSize, value)
            : (f.studyGroupSize ?? []).filter((v) => v !== value);

        f.studyGroupSize = emptyToUndefined(f.studyGroupSize);
        searchService.setFilters(f);
    };

    // STUDY PERIOD (numbers)
    const selectStudyPeriod = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseInt(event.target.value, 10);
        const f = { ...searchService.state.filters } as IFilters;
        f.studyPeriod = event.target.checked
            ? toggle(f.studyPeriod, value)
            : (f.studyPeriod ?? []).filter((v) => v !== value);

        f.studyPeriod = emptyToUndefined(f.studyPeriod);
        searchService.setFilters(f);
    };

    // CITATIONS (numbers)
    const selectAmountOfCitations = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseInt(event.target.value, 10);
        const f = { ...searchService.state.filters } as IFilters;
        f.amountOfCitations = event.target.checked
            ? toggle(f.amountOfCitations, value)
            : (f.amountOfCitations ?? []).filter((v) => v !== value);

        f.amountOfCitations = emptyToUndefined(f.amountOfCitations);
        searchService.setFilters(f);
    };

    useEffect(() => {
        const sliderAgeLabel = document.getElementById('slider-age-label');
        const componentWidth = Array.from(sliderAgeLabel?.children ?? []).at(0)?.clientWidth;
        if (sliderAgeLabel && componentWidth) {
            const paddingLeft = calculatePaddingPercentageLeft(
                SearchService.MIN_AGE,
                SearchService.MAX_AGE,
                searchService.state.filters.minAge,
                searchService.state.filters.maxAge,
            );
            const paddingRight = 100 - paddingLeft;
            const componentWidthLeft = (paddingLeft / 100) * componentWidth;
            const componentWidthRight = (paddingRight / 100) * componentWidth;
            sliderAgeLabel.style.padding = `0px calc(${paddingRight}% - ${componentWidthRight}px) 0px calc(${paddingLeft}% - ${componentWidthLeft}px)`;
        }
    }, [searchService.state.filters.minAge, searchService.state.filters.maxAge]);
    useEffect(() => {
        const sliderPublicationYearLabel = document.getElementById('slider-publication-year-label');
        const componentWidth = Array.from(sliderPublicationYearLabel?.children ?? []).at(0)?.clientWidth;
        if (sliderPublicationYearLabel && componentWidth) {
            const paddingLeft = calculatePaddingPercentageLeft(
                SearchService.MIN_PUBLICATION_YEAR,
                2099,
                searchService.state.filters.minPublicationYear > 2000
                    ? round(
                          ((searchService.state.filters.minPublicationYear - 2000) /
                              (SearchService.MAX_PUBLICATION_YEAR - 2000)) *
                              99 +
                              2000,
                      )
                    : searchService.state.filters.minPublicationYear,
                searchService.state.filters.maxPublicationYear > 2000
                    ? round(
                          ((searchService.state.filters.maxPublicationYear - 2000) /
                              (SearchService.MAX_PUBLICATION_YEAR - 2000)) *
                              99 +
                              2000,
                      )
                    : searchService.state.filters.maxPublicationYear,
            );
            const paddingRight = 100 - paddingLeft;
            const componentWidthLeft = (paddingLeft / 100) * componentWidth;
            const componentWidthRight = (paddingRight / 100) * componentWidth;
            sliderPublicationYearLabel.style.padding = `0px calc(${paddingRight}% - ${componentWidthRight}px) 0px calc(${paddingLeft}% - ${componentWidthLeft}px)`;
        }
    }, [searchService.state.filters.minPublicationYear, searchService.state.filters.maxPublicationYear]);

    const OLD_ETHN_GROUPS = [
        {
            code: 'WesternEuropean',
            countries: ['United Kingdom', 'Ireland', 'Germany', 'France', 'Netherlands', 'Belgium', 'Luxembourg'],
        },
        { code: 'SouthernEuropean', countries: ['Portugal', 'Spain', 'Italy', 'Greece', 'Cyprus', 'Malta'] },
        { code: 'CentralEuropean', countries: ['Poland', 'Hungary', 'Slovakia', 'Czechia', 'Austria', 'Switzerland'] },
        { code: 'NorthernEuropean', countries: ['Iceland', 'Norway', 'Sweden', 'Finland', 'Denmark'] },
        { code: 'Baltic', countries: ['Estonia', 'Latvia', 'Lithuania'] },
        { code: 'EasternEuropean', countries: ['Ukraine', 'Belarus', 'Russia', 'Moldova'] },
        {
            code: 'Balkan',
            countries: [
                'Romania',
                'Bulgaria',
                'Slovenia',
                'Croatia',
                'Bosnia and Herzegovina',
                'Montenegro',
                'Albania',
                'Serbia',
                'Kosovo',
                'North Macedonia',
            ],
        },
        { code: 'Caucasian', countries: ['Kazakhstan', 'Kyrgyzstan', 'Tajikistan', 'Uzbekistan', 'Turkmenistan'] },

        { code: 'NorthernAfrican', countries: ['Morocco', 'Algeria', 'Tunisia', 'Libya', 'Egypt', 'Sudan'] },
        {
            code: 'WestAfrican',
            countries: [
                'Mauritania',
                'Mali',
                'Niger',
                'Burkina Faso',
                'Benin',
                'Togo',
                'Ghana',
                'Ivory Coast',
                'Liberia',
                'Sierra Leone',
                'Guinea',
                'Guinea-Bissau',
                'Senegal',
                'Cape Verde',
                'Gambia',
            ],
        },
        {
            code: 'EastAfrican',
            countries: ['Kenya', 'Tanzania', 'Zambia', 'Malawi', 'Zimbabwe', 'Mozambique', 'Madagascar'],
        },
        { code: 'SouthAfrican', countries: ['Namibia', 'Botswana', 'South Africa'] },
        {
            code: 'CentralAfrican',
            countries: [
                'Angola',
                'Cameroon',
                'Central African Republic',
                'Chad',
                'Democratic Republic of Congo',
                'Republic of Congo',
                'Equatorial Guinea',
                'Gabon',
                'Sao Tome and Principe',
                'South Sudan',
                'Rwanda',
                'Burundi',
            ],
        },
        { code: 'HornOfAfrica', countries: ['Ethiopia', 'Eritrea', 'Djibouti', 'Somalia'] },

        {
            code: 'SouthwestAsian',
            countries: [
                'Armenia',
                'Azerbaijan',
                'Bahrain',
                'United Arab Emirates',
                'Georgia',
                'Iraq',
                'Israel',
                'Jordan',
                'Kuwait',
                'Lebanon',
                'Oman',
                'Qatar',
                'Saudi Arabia',
                'Syria',
                'Turkey',
                'Yemen',
            ],
        },
        {
            code: 'SouthAsian',
            countries: [
                'Iran',
                'Afghanistan',
                'Pakistan',
                'India',
                'Sri Lanka',
                'Nepal',
                'Bhutan',
                'Bangladesh',
                'Myanmar',
            ],
        },
        {
            code: 'Asian',
            countries: [
                'China',
                'North Korea',
                'South Korea',
                'Japan',
                'Mongolia',
                'Vietnam',
                'Laos',
                'Cambodia',
                'Malaysia',
                'Singapore',
                'Indonesia',
                'Philippines',
                'East Timor',
                'Taiwan',
            ],
        },

        { code: 'Oceanian', countries: ['Australia', 'New Zealand'] },
        { code: 'NorthAmerican', countries: ['US', 'Canada', 'Mexico'] },
        {
            code: 'LatinAmerican',
            countries: [
                'Argentina',
                'Bolivia',
                'Brazil',
                'Chile',
                'Colombia',
                'Ecuador',
                'Guyana',
                'Paraguay',
                'Peru',
                'Suriname',
                'Uruguay',
                'Venezuela',
                'Belize',
                'Costa Rica',
                'El Salvador',
                'Guatemala',
                'Honduras',
                'Nicaragua',
                'Panama',
                'Cuba',
                'Dominican Republic',
                'Haiti',
                'Jamaica',
                'Guadeloupe',
                'Barbados',
                'Trinidad and Tobago',
                'Virgin Islands',
            ],
        },
    ];

    // For fast lookup
    const ETH_COUNTRY_MAP = Object.fromEntries(OLD_ETHN_GROUPS.map((g) => [g.code, g.countries]));

    const ALL_ETH_CODES: NonNullable<IFilters['ethnicity']>[number][] = [
        'NorthernAfrican',
        'SouthernEuropean',
        'CentralEuropean',
        'Asian',
        'SouthAfrican',
        'SouthAsian',
        'WesternEuropean',
        'SouthwestAsian',
        'LatinAmerican',
        'WestAfrican',
        'NorthernEuropean',
        'Oceanian',
        'EastAfrican',
        'Balkan',
        'HornOfAfrica',
        'NorthAmerican',
        'CentralAfrican',
        'Baltic',
        'EasternEuropean',
        'Caucasian',
    ];

    // Ethnicities used by the new layout
    const USED_ETH_CODES = new Set<string>([
        'CentralEuropean',
        'WesternEuropean',
        'NorthernEuropean',
        'SouthernEuropean',
        'NorthernAfrican',
        'WestAfrican',
        'EastAfrican',
        'SouthAfrican',
        'Asian',
        'LatinAmerican',
        'Oceanian',
        'SouthAsian',
        'SouthwestAsian',
        'NorthAmerican',
    ]);

    // Compute leftover ethnicities (these go to Overig)
    const OVERIG_CODES: NonNullable<IFilters['ethnicity']>[number][] = ALL_ETH_CODES.filter(
        (code) => !USED_ETH_CODES.has(code),
    );

    // NEW LAYOUT MAPPING
    const REGIONAL_STRUCTURE: RegionBlock[] = [
        {
            id: 'EU',
            region: 'Europa',
            autoEthnicities: [
                'WesternEuropean',
                'NorthernEuropean',
                'SouthernEuropean',
                'EasternEuropean',
                'CentralEuropean',
                'Baltic',
                'Balkan',
                'Caucasian',
            ],
            groups: [
                { label: 'Oost', type: 'ethnicity', codes: ['EasternEuropean'] },
                { label: 'West', type: 'ethnicity', codes: ['WesternEuropean'] },
                { label: 'Noord', type: 'ethnicity', codes: ['NorthernEuropean'] },
                { label: 'Zuid', type: 'ethnicity', codes: ['SouthernEuropean'] },
            ],
        },
        {
            id: 'AS',
            region: 'Azië',
            autoEthnicities: ['Asian', 'SouthAsian', 'SouthwestAsian'],
            groups: [
                {
                    label: 'China',
                    type: 'countrylist',
                    countries: ['China', 'Hong Kong', 'Macao', 'Province of China'],
                },
                { label: 'Japan', type: 'countrylist', countries: ['Japan'] },
                {
                    label: 'Zuid-Oost',
                    type: 'countrylist',
                    countries: [
                        'Viet Nam',
                        'Thailand',
                        'Malaysia',
                        'Cambodia',
                        'Indonesia',
                        'Philippines',
                        'Singapore',
                        'Myanmar',
                    ],
                },
                {
                    label: 'Zuid-West',
                    type: 'countrylist',
                    countries: [
                        'India',
                        'Iran',
                        'Pakistan',
                        'Afghanistan',
                        'Nepal',
                        'Sri Lanka',
                        'Bhutan',
                        'Bangladesh',
                        'Maldives',
                    ],
                },
                {
                    label: 'Midden-Oosten',
                    type: 'countrylist',
                    countries: [
                        'Saudi Arabia',
                        'Yemen',
                        'Israel',
                        'Jordan',
                        'Iraq',
                        'Kuwait',
                        'Lebanon',
                        'Oman',
                        'Qatar',
                        'Bahrain',
                        'Palestine',
                        'United Arab Emirates',
                    ],
                },
            ],
        },
        {
            id: 'AF',
            region: 'Afrika',
            groups: [
                { label: 'Noord', type: 'ethnicity', codes: ['NorthernAfrican'] },
                { label: 'West', type: 'ethnicity', codes: ['WestAfrican'] },
                { label: 'Oost', type: 'ethnicity', codes: ['EastAfrican'] },
                { label: 'Zuid', type: 'ethnicity', codes: ['SouthAfrican'] },
            ],
        },
        {
            id: 'NA',
            region: 'Noord-Amerika',
            autoEthnicities: ['NorthAmerican'],
            groups: [
                { label: 'Europees', type: 'countrylist', countries: ['United States', 'Canada'] },
                { label: 'African', type: 'countrylist', countries: ['United States'] },
                { label: 'Indian', type: 'countrylist', countries: ['United States'] },
                { label: 'Inuit', type: 'countrylist', countries: ['Greenland', 'Canada'] },
                { label: 'Mexico', type: 'countrylist', countries: ['Mexico'] },
            ],
        },
        {
            id: 'CA',
            region: 'Midden-Amerika',
            noSubgroups: true,
            noAutoCountries: true,
            autoEthnicities: ['LatinAmerican'],
            groups: [
                {
                    label: 'Midden-Amerika',
                    type: 'countrylist',
                    countries: ['Costa Rica', 'Guatemala', 'El Salvador', 'Honduras', 'Panama'],
                },
            ],
        },
        {
            id: 'SA',
            region: 'Zuid-Amerika',
            groups: [
                { label: 'Brazilië', type: 'countrylist', countries: ['Brazil'] },
                {
                    label: 'Caraïben',
                    type: 'countrylist',
                    countries: [
                        'Cuba',
                        'Dominican Republic',
                        'Haiti',
                        'Jamaica',
                        'Barbados',
                        'Guadeloupe',
                        'Trinidad and Tobago',
                        'Virgin Islands',
                    ],
                },
                { label: 'Europese', type: 'countrylist', countries: ['Argentina', 'Chile', 'Uruguay'] },
                { label: 'Indian', type: 'countrylist', countries: ['Bolivia', 'Peru', 'Ecuador'] },
                { label: 'African', type: 'countrylist', countries: ['Suriname', 'Haiti', 'Dominican Republic'] },
                { label: 'Mixt', type: 'countrylist', countries: ['Colombia', 'Venezuela'] },
            ],
        },
        {
            id: 'AU',
            region: 'Australië',
            autoEthnicities: ['Oceanian'],
            noAutoCountries: true,
            groups: [
                { label: 'Europese', type: 'countrylist', countries: ['Australia'] },
                { label: 'Aborigen', type: 'countrylist', countries: ['Australia'] },
            ],
        },
        {
            id: 'NZ',
            region: 'Nieuw-Zeeland',
            autoEthnicities: ['Oceanian'],
            noAutoCountries: true,
            groups: [
                { label: 'Europese', type: 'countrylist', countries: ['New Zealand'] },
                { label: 'Maori', type: 'countrylist', countries: ['New Zealand'] },
            ],
        },
        {
            id: 'OT',
            region: 'Overig',
            noSubgroups: true,
            noAutoCountries: true,
            groups: [
                {
                    label: 'Overig',
                    type: 'ethnicity',
                    codes: OVERIG_CODES,
                },
            ],
        },
    ];

    const selectRegionGroup = (group: RegionGroup, checked: boolean, region: RegionBlock) => {
        const f = { ...searchService.state.filters } as IFilters;

        if (group.type === 'ethnicity') {
            const codes = group.codes;

            if (checked) {
                f.ethnicity = [...new Set([...(f.ethnicity ?? []), ...codes])];

                const addedCountries = codes.flatMap((c) => ETH_COUNTRY_MAP[c] ?? []);
                f.countries = [...new Set([...(f.countries ?? []), ...addedCountries])];
            } else {
                f.ethnicity = (f.ethnicity ?? []).filter((e) => !codes.includes(e));

                const removedCountries = codes.flatMap((c) => ETH_COUNTRY_MAP[c] ?? []);
                f.countries = (f.countries ?? []).filter((c) => !removedCountries.includes(c));
            }
        }

        if (group.type === 'countrylist') {
            if (checked) {
                f.countries = [...new Set([...(f.countries ?? []), ...group.countries])];
            } else {
                f.countries = (f.countries ?? []).filter((c) => !group.countries.includes(c));
            }
        }

        // refresh region open state when subgroup changes
        setOpenRegions((r) => ({ ...r, [region.id]: true }));

        f.ethnicity = emptyToUndefined(f.ethnicity);
        f.countries = emptyToUndefined(f.countries);

        searchService.setFilters(f);
    };

    const getRegionPayload = (region: RegionBlock) => {
        const allCodes: NonNullable<IFilters['ethnicity']>[number][] = [];
        const allCountries: string[] = [];

        const shouldIncludeCountries = !region.noAutoCountries;

        for (const g of region.groups) {
            if (g.type === 'ethnicity') {
                allCodes.push(...g.codes);

                if (shouldIncludeCountries) {
                    for (const code of g.codes) {
                        allCountries.push(...(ETH_COUNTRY_MAP[code] ?? []));
                    }
                }
            }

            if (g.type === 'countrylist' && shouldIncludeCountries) {
                allCountries.push(...g.countries);
            }
        }

        // ⭐ ADD THIS: auto-ethnicities for region
        if (region.autoEthnicities?.length) {
            allCodes.push(...region.autoEthnicities);

            if (shouldIncludeCountries) {
                for (const code of region.autoEthnicities) {
                    allCountries.push(...(ETH_COUNTRY_MAP[code] ?? []));
                }
            }
        }

        return {
            codes: Array.from(new Set(allCodes)),
            countries: Array.from(new Set(allCountries)),
        };
    };

    const selectRegion = (region: RegionBlock, checked: boolean) => {
        const f = { ...searchService.state.filters } as IFilters;
        const payload = getRegionPayload(region);

        if (checked) {
            setOpenRegions((r) => ({ ...r, [region.id]: true }));
            f.ethnicity = [...new Set([...(f.ethnicity ?? []), ...payload.codes])];
            if (!region.noAutoCountries) {
                f.countries = [...new Set([...(f.countries ?? []), ...payload.countries])];
            }
        } else {
            setOpenRegions((r) => ({ ...r, [region.id]: false }));
            f.ethnicity = (f.ethnicity ?? []).filter((e) => !payload.codes.includes(e));
            if (!region.noAutoCountries) {
                f.countries = (f.countries ?? []).filter((c) => !payload.countries.includes(c));
            }
        }

        f.ethnicity = emptyToUndefined(f.ethnicity);
        f.countries = emptyToUndefined(f.countries);

        searchService.setFilters(f);
    };

    const handleStyle = {
        width: 5,
        height: 16,
        borderRadius: 5,
        backgroundColor: '#000',
        border: 'none',
        // invisible hit area
        boxShadow: '0 0 0 10px rgba(0,0,0,0)',
    };

    return (
        <div className="FilterSection">
            <h3>{t('components.filters.filters')}</h3>
            <p className="body body-normal">{t('components.filters.gender')}</p>
            <div>
                <label className="mark">
                    <input
                        type="checkbox"
                        value="female"
                        name={'checkbox-filter-gender'}
                        onChange={selectGender}
                        checked={(searchService.state.filters.gender ?? []).includes('female')}
                    />

                    <span className="dot" />
                    <p className="body body-small">{t('components.filters.female')}</p>
                </label>
                <label className="mark">
                    <input
                        type="checkbox"
                        value="male"
                        name={'checkbox-filter-gender'}
                        onChange={selectGender}
                        checked={(searchService.state.filters.gender ?? []).includes('male')}
                    />

                    <span className="dot" />
                    <p className="body body-small">{t('components.filters.male')}</p>
                </label>
            </div>
            <p className="body body-normal">{t('components.filters.age')}</p>
            <div className="slider">
                <div id="slider-age" className="slider-wrapper">
                    <p className="body body-small">{SearchService.MIN_AGE}</p>
                    <Slider
                        range
                        allowCross={false}
                        min={SearchService.MIN_AGE}
                        max={SearchService.MAX_AGE}
                        value={[searchService.state.filters.minAge, searchService.state.filters.maxAge]}
                        onChange={(v) => selectAge(v as number[])}
                        handleStyle={[handleStyle, handleStyle]}
                        pushable={1}
                    />
                    <p className="body body-small">{SearchService.MAX_AGE}</p>
                </div>
                <label id="slider-age-label" htmlFor="#slider-age">
                    <p className="body body-small">{`${searchService.state.filters.minAge} - ${searchService.state.filters.maxAge}`}</p>
                </label>
            </div>
            <p className="body body-normal">{t('components.filters.study-design')}</p>
            <div>
                <label className="mark">
                    <input
                        type="checkbox"
                        value="meta-analysis"
                        name={'checkbox-filter-study-design'}
                        onChange={selectStudyDesign}
                        checked={(searchService.state.filters.studyDesign ?? []).includes('meta-analysis')}
                    />

                    <span className="dot" />
                    <p className="body body-small">{t('components.filters.meta-analysis')}</p>
                </label>
                <label className="mark">
                    <input
                        type="checkbox"
                        value="RCT"
                        name={'checkbox-filter-study-design'}
                        onChange={selectStudyDesign}
                        checked={(searchService.state.filters.studyDesign ?? []).includes('RCT')}
                    />

                    <span className="dot" />
                    <p className="body body-small">{t('components.filters.rct')}</p>
                </label>
            </div>
            <p className="body body-normal">{t('components.filters.publication-year')}</p>
            <div className="slider">
                <div id="slider-publication-year" className="slider-wrapper">
                    <p className="body body-small">{SearchService.MIN_PUBLICATION_YEAR}</p>
                    <Slider
                        range
                        allowCross={false}
                        min={SearchService.MIN_PUBLICATION_YEAR}
                        max={2099}
                        value={[
                            searchService.state.filters.minPublicationYear > 2000
                                ? round(
                                      ((searchService.state.filters.minPublicationYear - 2000) /
                                          (SearchService.MAX_PUBLICATION_YEAR - 2000)) *
                                          99 +
                                          2000,
                                  )
                                : searchService.state.filters.minPublicationYear,
                            searchService.state.filters.maxPublicationYear > 2000
                                ? round(
                                      ((searchService.state.filters.maxPublicationYear - 2000) /
                                          (SearchService.MAX_PUBLICATION_YEAR - 2000)) *
                                          99 +
                                          2000,
                                  )
                                : searchService.state.filters.maxPublicationYear,
                        ]}
                        onChange={(v) => selectPublicationYear(v as number[])}
                        handleStyle={[handleStyle, handleStyle]}
                        pushable={1}
                    />
                    <p className="body body-small">{t('components.filters.present')}</p>
                </div>
                <label id="slider-publication-year-label" htmlFor="#slider-publication-year">
                    <p className="body body-small">{`${searchService.state.filters.minPublicationYear} - ${
                        searchService.state.filters.maxPublicationYear === SearchService.MAX_PUBLICATION_YEAR
                            ? t('components.filters.present')
                            : searchService.state.filters.maxPublicationYear
                    }`}</p>
                </label>
            </div>
            <p className="body body-normal">{t('components.filters.study-group-size')}</p>
            <div>
                <label className="mark">
                    <input
                        type="checkbox"
                        value={500}
                        name={'checkbox-filter-study-group-size'}
                        onChange={selectStudySize}
                        checked={(searchService.state.filters.studyGroupSize ?? []).includes(500)}
                    />

                    <span className="dot" />
                    <p className="body body-small">{t('components.filters.more-than')} 500</p>
                </label>
                <label className="mark">
                    <input
                        type="checkbox"
                        value={2000}
                        name={'checkbox-filter-study-group-size'}
                        onChange={selectStudySize}
                        checked={(searchService.state.filters.studyGroupSize ?? []).includes(2000)}
                    />

                    <span className="dot" />
                    <p className="body body-small">{t('components.filters.more-than')} 2.000</p>
                </label>
                <label className="mark">
                    <input
                        type="checkbox"
                        value={10000}
                        name={'checkbox-filter-study-group-size'}
                        onChange={selectStudySize}
                        checked={(searchService.state.filters.studyGroupSize ?? []).includes(10000)}
                    />

                    <span className="dot" />
                    <p className="body body-small">{t('components.filters.more-than')} 10.000</p>
                </label>
            </div>
            <p className="body body-normal">{t('components.filters.study-period')}</p>
            <div>
                <label className="mark">
                    <input
                        type="checkbox"
                        value={1}
                        name={'checkbox-filter-study-period'}
                        onChange={selectStudyPeriod}
                        checked={(searchService.state.filters.studyPeriod ?? []).includes(1)}
                    />

                    <span className="dot" />
                    <p className="body body-small">
                        {t('components.filters.more-than')} 1 {t('components.filters.year')}
                    </p>
                </label>
                <label className="mark">
                    <input
                        type="checkbox"
                        value={2}
                        name={'checkbox-filter-study-period'}
                        onChange={selectStudyPeriod}
                        checked={(searchService.state.filters.studyPeriod ?? []).includes(2)}
                    />

                    <span className="dot" />
                    <p className="body body-small">
                        {t('components.filters.more-than')} 2 {t('components.filters.year')}
                    </p>
                </label>
                <label className="mark">
                    <input
                        type="checkbox"
                        value={5}
                        name={'checkbox-filter-study-period'}
                        onChange={selectStudyPeriod}
                        checked={(searchService.state.filters.studyPeriod ?? []).includes(5)}
                    />

                    <span className="dot" />
                    <p className="body body-small">
                        {t('components.filters.more-than')} 5 {t('components.filters.year')}
                    </p>
                </label>
            </div>
            <p className="body body-normal">{t('components.filters.amount-of-citations')}</p>
            <div>
                <label className="mark">
                    <input
                        type="checkbox"
                        value={50}
                        name={'checkbox-filter-amount-of-citations'}
                        onChange={selectAmountOfCitations}
                        checked={(searchService.state.filters.amountOfCitations ?? []).includes(50)}
                    />

                    <span className="dot" />
                    <p className="body body-small">{t('components.filters.more-than')} 50</p>
                </label>
            </div>
            <p className="body body-normal">{t('components.filters.ethnicity.header')}</p>

            <div className="ethnicity-list">
                {REGIONAL_STRUCTURE.map((region) => {
                    const payload = getRegionPayload(region);

                    // is region fully selected?
                    const isRegionChecked =
                        payload.codes.every((c) => (searchService.state.filters.ethnicity ?? []).includes(c)) &&
                        payload.countries.every((c) => (searchService.state.filters.countries ?? []).includes(c));

                    // is region partially selected?
                    const isRegionPartial =
                        !isRegionChecked &&
                        (payload.codes.some((c) => (searchService.state.filters.ethnicity ?? []).includes(c)) ||
                            payload.countries.some((c) => (searchService.state.filters.countries ?? []).includes(c)));

                    return (
                        <div key={region.id} className="region-block">
                            <label className="mark">
                                <input
                                    type="checkbox"
                                    checked={isRegionChecked}
                                    ref={(el) => {
                                        if (el) el.indeterminate = isRegionPartial;
                                    }}
                                    onChange={(e) => selectRegion(region, e.target.checked)}
                                />
                                <span className="dot" />
                                <p className="body body-small">{region.region}</p>
                            </label>

                            {!region.noSubgroups && openRegions[region.id] && (
                                <div className="subgroups">
                                    {region.groups.map((group) => {
                                        const isChecked =
                                            group.type === 'ethnicity'
                                                ? group.codes.every((code) =>
                                                      (searchService.state.filters.ethnicity ?? []).includes(code),
                                                  )
                                                : group.countries.every((country) =>
                                                      (searchService.state.filters.countries ?? []).includes(country),
                                                  );

                                        return (
                                            <label key={group.label} className="mark mark--nested">
                                                <input
                                                    type="checkbox"
                                                    checked={isChecked}
                                                    onChange={(e) => selectRegionGroup(group, e.target.checked, region)}
                                                />
                                                <span className="dot" />
                                                <p className="body body-small">{group.label}</p>
                                            </label>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default FilterSection;
